import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { collectionData, collectionGroup, deleteDoc, doc, DocumentData, getDoc, getFirestore, query, QuerySnapshot, snapToData, where } from '@angular/fire/firestore';
import { HttpService, ObservableQueryService } from '@damofire/data-angular-commons';
import { ID_FIELD_OPTIONS, MAX_QUERY_RESULTS, USERS_COLLECTION, USERS_LINKS_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService, DivisionRole, OrganizationLink, User } from '@damofire/data-security';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Division } from './division.dto';
import { FlatOrganizationLink } from './link.dto';
import { PartialOrganization } from './organization.dto';
import { Representative, WAREHOUSE_USER_ROLES } from './user.dto';

export interface RepresentativeOf {
  _id?: string;
  types?: Representative[];
}

export type UserRepOf = User & Partial<RepresentativeOf>;

@Injectable()
export abstract class LinkService {
  abstract searchForUsersLinks(divisionId: string, organizationId: string, name?: string): Promise<FlatOrganizationLink[]>;
  abstract create(organization: PartialOrganization, divisionRole: DivisionRole, user: User): Promise<void>;
  abstract update(link: OrganizationLink, divisionRole: DivisionRole): Promise<void>;
  abstract get(userId: string, organizationId: string): Promise<OrganizationLink>;
  abstract getOrganizationPeopleCount(orgId: string): Observable<number>;
  abstract getDivisionPeopleCount(divId: string): Observable<number>;
  abstract getLinksByRoles(divId: string, roles: string[]): Observable<OrganizationLink[]>;
  abstract delete(link: OrganizationLink, divisionId?: string): Promise<void>;
  abstract deleteRole(userId: string, link: DivisionRole): Promise<void>;
}

@Injectable()
export class FirebaseLinkService implements LinkService {
  constructor(
    private readonly http: HttpService,
    private readonly queryService: ObservableQueryService,
  ) {}

  async get(userId: string, organizationId: string): Promise<OrganizationLink> {
    let link: OrganizationLink;
    const linkRef = `${USERS_COLLECTION}/${userId}/${USERS_LINKS_COLLECTION}/${organizationId}`;
    const d = await getDoc(doc(getFirestore(), linkRef));
    if (d.exists()) {
      link = snapToData(d, ID_FIELD_OPTIONS);
    }
    return link;
  }

  getOrganizationPeopleCount(orgId: string): Observable<number> {
    return this.getOrganizationLinks(orgId).pipe(map((orgLinks: OrganizationLink[]) => orgLinks.length));
  }

  getDivisionPeopleCount(divId: string): Observable<number> {
    return this.getDivisionLinks(divId).pipe(map((orgLinks: OrganizationLink[]) => orgLinks.length));
  }

  getLinksByRoles(divId: string, roles: string[]): Observable<OrganizationLink[]> {
    return collectionData<OrganizationLink>(query(collectionGroup(getFirestore(), USERS_LINKS_COLLECTION), where(`divisions.${divId}.role`, 'in', roles)));
  }

  getOrganizationLinks(orgId: string): Observable<OrganizationLink[]> {
    return collectionData<OrganizationLink>(query(collectionGroup(getFirestore(), USERS_LINKS_COLLECTION), where(`_id`, '==', orgId)), ID_FIELD_OPTIONS);
  }

  getDivisionLinks(divId: string): Observable<OrganizationLink[]> {
    return collectionData<OrganizationLink>(query(collectionGroup(getFirestore(), USERS_LINKS_COLLECTION), where(`divisions.${divId}`, '!=', null)), ID_FIELD_OPTIONS).pipe(
      map((links: OrganizationLink[]) => {
        return links.filter((link) => link.divisions && link.divisions[divId]);
      }),
    );
  }

  async searchForUsersLinks(divisionId: string, organizationId: string, name = ''): Promise<FlatOrganizationLink[]> {
    const divisionLinks = this.getDivisionLinks(divisionId).pipe(
      take(1),
      map((links) => {
        return links.map((link) => ({ ...link, division: link.divisions[divisionId] }));
      }),
    );
    const orgAdminLinks = this.getOrganizationLinks(organizationId).pipe(
      take(1),
      map((links) => {
        const adminLinks = links.filter((link) => link.admin);
        return adminLinks.map((link) => ({ ...link, division: null }));
      }),
    );

    const mergedLinks = (await divisionLinks.toPromise()).concat(await orgAdminLinks.toPromise());
    const filteredLinks = await this.queryService.search<FlatOrganizationLink>(
      mergedLinks,
      {
        searchPhrase: name,
        from: 0,
        size: MAX_QUERY_RESULTS,
        sort: { 'user.firstName': 'asc', 'user.lastName': 'asc' },
      },
      ['user.firstName', 'user.lastName'],
    );

    // We make sure the same user doesn't get returned more than once.
    // This is not supposed to happen because admins should not have links to divisions but they do at the moment
    return filteredLinks.result.filter((links: FlatOrganizationLink, index, self) => {
      return index === self.findIndex((t: FlatOrganizationLink) => t.user._id === links.user._id);
    });
  }

  async create(organization: PartialOrganization, divisionRole: DivisionRole, user: User): Promise<void> {
    return this.http.post(`stas-links/user/${user._id}/organization/${organization._id}`, { divisionRole });
  }

  async update(link: OrganizationLink, divisionRole: DivisionRole): Promise<void> {
    return this.http.put(`stas-links/user/${link.user._id}/organization/${link._id}`, { divisionRole });
  }

  async delete(link: OrganizationLink, divisionId?: string): Promise<void> {
    if (!divisionId) {
      return deleteDoc(doc(getFirestore(), `${USERS_COLLECTION}/${link.user._id}/${USERS_LINKS_COLLECTION}/${link._id}`));
    } else {
      const params = new HttpParams().set('divisionId', divisionId);
      return this.http.delete(`stas-links/user/${link.user._id}/organization/${link._id}`, params);
    }
  }

  async deleteRole(userId: string, link: DivisionRole): Promise<void> {
    const params = new HttpParams().set('divisionId', link._id);
    return this.http.delete(`stas-links/user/${userId}/organization/${link.orgId}`, params);
  }
}

export const createRepUserDivisionLink = async (user: UserRepOf, userLinkSnaps: QuerySnapshot, dtoSigner: AuthDtoSignerService, division: Division) => {
  if (userLinkSnaps.empty) {
    console.log('no links found with given conditions, creating new link');
    const divisions: { [key: string]: DivisionRole } = {};
    divisions[division._id] = {
      _id: division._id,
      name: division.name,
      role: WAREHOUSE_USER_ROLES.REPRESENTATIVE,
      enabled: !!division.enabled,
    };
    await dtoSigner.set(
      {
        _id: division.organization._id,
        name: division.organization.name,
        divisions,
        user: {
          _id: user._id,
          email: user.email,
          enabled: user.enabled,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone ?? '',
        },
      },
      `${USERS_COLLECTION}/${user._id}/${USERS_LINKS_COLLECTION}/${division.organization._id}`,
    );
  } else {
    for (const link of userLinkSnaps.docs) {
      console.log('adding division to existing user-organization link');
      const divisions = link.data().divisions ?? {};
      divisions[division._id] = {
        _id: division._id,
        name: division.name,
        ...(division._id in divisions && divisions[division._id]),
        role: WAREHOUSE_USER_ROLES.REPRESENTATIVE,
        enabled: !!division.enabled,
      };
      await dtoSigner.update({ divisions }, link.ref.path);
    }
  }
};

export const removeUserDivisionLink = async (userLinkSnaps: QuerySnapshot, dtoSigner: AuthDtoSignerService, division: Division) => {
  if (userLinkSnaps.empty) {
    console.warn('Trying to delete a link that cannot be found');
  }

  for (const link of userLinkSnaps.docs) {
    console.log('removing division from user-organization link');
    const { divisions, user, _id, admin } = link.data();
    delete divisions[division._id];
    const userSnap = await getDoc<User, DocumentData>(doc(getFirestore(), `${USERS_COLLECTION}/${user._id}`));
    const { organization: userOrg } = userSnap.data();
    //  Delete link if no divisions in link + user-home-org !== current division org.
    if (Object.keys(divisions).length === 0 && userOrg?._id !== division.organization?._id && !admin) {
      deleteDoc(doc(getFirestore(), `${USERS_COLLECTION}/${user._id}/links/${_id}`)).catch(console.error);
    } else {
      dtoSigner.update({ divisions }, link.ref.path).catch(console.error);
    }
  }
};
